import { ArrowLeft, ArrowRight, BookOpen, Calendar, Search } from 'lucide-react';
import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600' }
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    fetchSettings();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/posts?limit=100`);
      const data = await response.json();
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/settings`);
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getColorConfig = (colorValue) => {
    return categoryColors[colorValue] || categoryColors.gray;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-950 via-red-900 to-orange-900">
        <div className="container mx-auto px-4 py-6">
          <button
            onClick={() => window.location.href = '/'}
            className="flex items-center gap-2 text-white hover:text-orange-300 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            Ana Sayfaya Dön
          </button>
        </div>
        
        {/* Hero */}
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="inline-flex items-center gap-3 bg-white/10 text-white px-6 py-3 rounded-full font-bold text-lg mb-6">
            <BookOpen className="w-6 h-6" />
            <span>İsveç Ehliyet Rehberi</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
            {settings?.section_title || 'Blog & Rehberler'}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {settings?.section_subtitle || "İsveç'te ehliyet almak isteyenler için faydalı bilgiler, ipuçları ve rehberler"}
          </p>
        </div>
      </div>

      {/* Search & Filter */}
      {(settings?.enable_search !== false || settings?.enable_categories !== false) && (
        <div className="container mx-auto px-4 -mt-8">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              {settings?.enable_search !== false && (
                <div className="flex-1 relative">
                  <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Yazılarda ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              )}
              {settings?.enable_categories !== false && categories.length > 0 && (
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="all">Tüm Kategoriler</option>
                  {categories.map(cat => (
                    <option key={cat.name} value={cat.name}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Blog Posts Grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {filteredPosts.map((post) => {
            const colorConfig = getColorConfig(post.category_color);
            return (
              <a
                key={post.id}
                href={`/blog/${post.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`h-48 bg-gradient-to-br ${post.gradient || 'from-red-600 to-orange-500'} flex items-center justify-center relative`}>
                  {post.image_url ? (
                    <img 
                      src={post.image_url.startsWith('/') ? `${API_URL}${post.image_url}` : post.image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-7xl">{post.emoji || '📝'}</span>
                  )}
                  {post.is_featured && (
                    <span className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded text-xs font-bold">
                      ⭐ Öne Çıkan
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-3 py-1 ${colorConfig.bg} ${colorConfig.text} rounded-full text-xs font-bold`}>
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-2 group-hover:text-red-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {post.created_at ? new Date(post.created_at).toLocaleDateString('tr-TR') : ''}
                    </span>
                    <span className="text-red-600 font-bold text-sm flex items-center gap-1 group-hover:gap-3 transition-all">
                      Oku <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Sonuç Bulunamadı</h3>
            <p className="text-gray-600">Arama kriterlerinize uygun yazı bulunamadı.</p>
          </div>
        )}
      </div>

      {/* Newsletter CTA */}
      <div className={`bg-gradient-to-r ${settings?.cta_gradient || 'from-red-600 to-orange-500'} py-16`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            {settings?.cta_title || 'İsveç Ehliyet Sınavına Hazır mısın?'}
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            {settings?.cta_subtitle || '2000+ güncel soru, Türkçe açıklamalar ve sınav simülasyonu ile başarıya ulaşın!'}
          </p>
          <button
            onClick={() => window.location.href = '/?action=login'}
            className="bg-white text-red-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center gap-2"
          >
            {settings?.cta_button_text || 'Ücretsiz Başla'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white/70 py-8">
        <div className="container mx-auto px-4 text-center">
          <p>© Ehliyet.nu - Tüm hakları saklıdır</p>
        </div>
      </footer>
    </div>
  );
}
